import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface UserPermissions {
  id?: string;
  user_id: string;
  company_id: string;
  criar_cliente: boolean;
  alterar_cliente: boolean;
  apagar_cliente: boolean;
  criar_regua: boolean;
  alterar_regua: boolean;
  apagar_regua: boolean;
  listar_usuario: boolean;
  alterar_usuario: boolean;
  apagar_usuario: boolean;
  alterar_configuracoes: boolean;
  ver_config_banco: boolean;
  ver_config_empresa: boolean;
  ver_config_email: boolean;
  ver_config_whatsapp: boolean;
}

export interface UserWithDetails {
  id: string;
  user_id: string;
  full_name: string | null;
  avatar_url: string | null;
  company_id: string | null;
  role: 'admin' | 'manager' | 'operator' | null;
  permissions: UserPermissions | null;
  email?: string;
}

export function useUsers() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Fetch users in the same company with their roles and permissions
  const usersQuery = useQuery({
    queryKey: ['users-with-details'],
    queryFn: async () => {
      // Get current user's company
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Não autenticado');

      const { data: profile } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('user_id', user.id)
        .single();

      if (!profile?.company_id) {
        return [];
      }

      // Get all profiles in the company
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('id, user_id, full_name, avatar_url, company_id')
        .eq('company_id', profile.company_id);

      if (profilesError) throw profilesError;

      // Get roles for these users
      const userIds = profiles?.map(p => p.user_id) || [];
      
      const { data: roles } = await supabase
        .from('user_roles')
        .select('user_id, role')
        .in('user_id', userIds);

      // Get permissions for these users
      const { data: permissions } = await supabase
        .from('user_permissions')
        .select('*')
        .in('user_id', userIds);

      // Combine data
      const usersWithDetails: UserWithDetails[] = (profiles || []).map(p => {
        const userRole = roles?.find(r => r.user_id === p.user_id);
        const userPerms = permissions?.find(perm => perm.user_id === p.user_id);
        
        return {
          ...p,
          role: userRole?.role || null,
          permissions: userPerms || null,
        };
      });

      return usersWithDetails;
    }
  });

  // Update user permissions
  const updatePermissionsMutation = useMutation({
    mutationFn: async ({ 
      userId, 
      permissions 
    }: { 
      userId: string; 
      permissions: Partial<UserPermissions>;
    }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Não autenticado');

      const { data: profile } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('user_id', user.id)
        .single();

      if (!profile?.company_id) throw new Error('Empresa não configurada');

      // Upsert permissions
      const { error } = await supabase
        .from('user_permissions')
        .upsert({
          user_id: userId,
          company_id: profile.company_id,
          ...permissions,
        }, {
          onConflict: 'user_id,company_id'
        });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users-with-details'] });
      toast({
        title: 'Permissões atualizadas',
        description: 'As permissões do usuário foram salvas com sucesso.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Erro ao salvar',
        description: error.message,
        variant: 'destructive',
      });
    }
  });

  // Update user role
  const updateRoleMutation = useMutation({
    mutationFn: async ({ 
      userId, 
      role 
    }: { 
      userId: string; 
      role: 'admin' | 'manager' | 'operator';
    }) => {
      // First delete existing role
      await supabase
        .from('user_roles')
        .delete()
        .eq('user_id', userId);

      // Insert new role
      const { error } = await supabase
        .from('user_roles')
        .insert({ user_id: userId, role });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users-with-details'] });
      toast({
        title: 'Cargo atualizado',
        description: 'O cargo do usuário foi alterado com sucesso.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Erro ao alterar cargo',
        description: error.message,
        variant: 'destructive',
      });
    }
  });

  // Update user profile
  const updateProfileMutation = useMutation({
    mutationFn: async ({ 
      profileId, 
      fullName 
    }: { 
      profileId: string; 
      fullName: string;
    }) => {
      const { error } = await supabase
        .from('profiles')
        .update({ full_name: fullName })
        .eq('id', profileId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users-with-details'] });
      toast({
        title: 'Perfil atualizado',
        description: 'O nome do usuário foi alterado com sucesso.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Erro ao salvar',
        description: error.message,
        variant: 'destructive',
      });
    }
  });

  return {
    users: usersQuery.data || [],
    isLoading: usersQuery.isLoading,
    error: usersQuery.error,
    updatePermissions: updatePermissionsMutation.mutate,
    updateRole: updateRoleMutation.mutate,
    updateProfile: updateProfileMutation.mutate,
    isUpdating: updatePermissionsMutation.isPending || updateRoleMutation.isPending || updateProfileMutation.isPending,
    refetch: usersQuery.refetch,
  };
}
